import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { setMadadContactModalState } from 'store/state/app/actions';
import { SetMadadContactModalStatePayload } from 'store/state/app/payloads';
import { getOfficeLink } from 'helpers/contact';
import { OfficeCard } from './OfficeCard';
import withEvents from 'analytics/withEvents';
import { useIntersectionObserver } from 'hooks/useIntersectionObserver';
import * as actions from 'store/state/router/actions';
import { NavigateToFn, Route } from 'config/routes';
import { MadadAgencyCard } from 'components/madad/entities';
import { CardLink } from './styled';
import { Link } from 'components/link';


interface WithDispatchProps {
  openContactModal: (payload: SetMadadContactModalStatePayload) => void;
  onCardClickEvent?: () => void;
  rootNode?: React.MutableRefObject<HTMLDivElement>;
  onCardExposeEvent?: () => void;
  navigateTo: NavigateToFn;
  withPropertyInfo?: boolean;
}

type MadadAgencyCardProps = MadadAgencyCard & {
  size?: 'medium' | 'large';
  agencyNameHeadingLevel?: 2 | 3;
  isCarousel?: boolean;
  pageSource: string;
  collectionSource?: string;
  cardIndex?: number;
  source: string;
};

export const AgencySmartCard: React.FC<MadadAgencyCardProps & WithDispatchProps> = props => {
  const {
    id,
    phone,
    agencyName,
    avatarImage,
    openContactModal,
    onCardClickEvent,
    rootNode,
    onCardExposeEvent,
    isCarousel,
    source,
  } = props;

  const [ isVisible, setIsVisible ] = useState(false);
  const cardItem = useRef<HTMLDivElement>(null);

  useIntersectionObserver(([ entry ]) => {
    setIsVisible(entry.isIntersecting);
  }, [ cardItem ], { threshold: 0, root: rootNode });

  useEffect(() => {
    if (isVisible) {
      onCardExposeEvent();
    }
  }, [ onCardExposeEvent, isVisible, id, agencyName ]);

  const href = getOfficeLink(id);

  const handleContactClick = () => {
    openContactModal({
      id,
      phone,
      isAgent: false,
      officeId: id,
      officeName: agencyName,
      officeLogoUrl: avatarImage,
      officeHref: href,
      eventSource: 'madad_card',
    });
  };

  return (
    <CardLink data-auto="agency-link" ref={cardItem}>
      <Link
        routeName={Route.OfficePage}
        routeParams={{ id, source }}
        onClick={onCardClickEvent}
        hideLinkParams
      >
        <OfficeCard
          {...props}
          onContactClick={handleContactClick}
          isCarousel={isCarousel}
        />
      </Link>
    </CardLink>
  );
};

const mapDispatchToProps = {
  openContactModal: setMadadContactModalState,
  navigateTo: actions.navigateTo,
};

const AgencySmartCardWithEvents = withEvents<MadadAgencyCardProps & WithDispatchProps>((sendEvent, props) => ({
  onCardClickEvent: () => {
    sendEvent('madad_card_click', 'madad', {
      event: {
        agency_details: {
          id: props.id,
          name: props.agencyName,
        },
        rank_type: props.topTenBadgeVariant === 'golden' ? 'top_10_gold' : 'top_10',
        page_source: props.pageSource,
        source: props.source,
        ...(props.cardIndex ? { card_position: props.cardIndex } : null),
        ...(props.collectionSource ? { collection_source: props.collectionSource } : null),
      },
    });
  },
  onCardExposeEvent: () => {
    sendEvent('madad_card_agency_expose', 'madad', {
      event: {
        agency_details: {
          id: props.id,
          name: props.agencyName,
        },
        rank_type: props.topTenBadgeVariant === 'golden' ? 'top_10_gold' : 'top_10',
        page_source: props.pageSource,
        source: props.source,
        ...(props.cardIndex ? { card_position: props.cardIndex } : null),
        ...(props.collectionSource ? { collection_source: props.collectionSource } : null),
      },
    });
  },
  openContactModal: (params: SetMadadContactModalStatePayload) => {
    sendEvent('user_contact_me_form_open', 'user', {
      event: {
        agent_details: {
          id: params.id,
          name: params.agentName,
          office_id: params.officeId,
          office_name: params.officeName,
          phone_number: params.phone,
        },
        source: 'madad_card',
      },
    });
  },
}))(AgencySmartCard);

export default connect(null, mapDispatchToProps)(AgencySmartCardWithEvents);
